import { QueryHookOptions, useQuery } from '@apollo/client';
import { ToggleReactionResponse } from 'component/style_item/type';
import { USER_REACTIONS } from 'graph_ql/user_reactions/user_reactions';
import { get, map, uniqBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import {
    setReactedItems as setReactedItemsAction,
    setUnProcessedReactedItems as setUnProcessedReactedItemsAction,
} from 'redux/style';
import { AlgoliaReaction, ReactionAlgoliaQuery, ReactionDocType, ReactionType } from './types';

export const useGetReactionsFromStore = () => {
    const dispatch = useDispatch();
    const { reactedItems, reactedItemsUnProcessed } = useSelector((state: RootState) => state.style);
    const setReactedItems = (data: AlgoliaReaction[]) => dispatch(setReactedItemsAction(data));
    const setUnProcessedReactedItems = (data: AlgoliaReaction[]) => dispatch(setUnProcessedReactedItemsAction(data));

    const updateAfterReaction = (response: ToggleReactionResponse, type: ReactionType) => {
        const { collectionName, docId } = response.toggleReaction;
        const { isAlreadyDislike, isAlreadyLike } = response.toggleReaction.reactionState;
        let newReacted: AlgoliaReaction[] = JSON.parse(JSON.stringify(reactedItems));
        if (collectionName === ReactionDocType.STYLE) {
            const items = newReacted.findIndex((r) => r.style.id === docId);
            newReacted[items].style.isLiked = isAlreadyLike;
            newReacted[items].style.isDisliked = isAlreadyDislike;
        }
        if (collectionName === ReactionDocType.ITEMS) {
            newReacted = newReacted.map((reaction) => {
                const index = get(reaction, ['style', 'items'], []).findIndex((item) => {
                    return item.id === docId;
                });

                if (index > -1) {
                    const newItems = [...reaction.style.items];
                    newItems[index].data.isLiked = isAlreadyLike;
                    newItems[index].data.isDisliked = isAlreadyDislike;
                    return { ...reaction, style: { ...reaction.style, items: newItems } };
                }
                return reaction;
            });
            const styleIndex = newReacted.findIndex((r) => r.style.id === docId);
            if (styleIndex > -1) {
                newReacted[styleIndex].style.isLiked = isAlreadyLike;
                newReacted[styleIndex].style.isDisliked = isAlreadyDislike;
            }
        }

        // Removing reactions with no reacted styles or items
        newReacted = newReacted.filter((reaction) => {
            if (
                (reaction.style.isLiked && type === ReactionType.Like) ||
                (reaction.style.isDisliked && type === ReactionType.Dislike)
            ) {
                return true;
            }

            // Find liked item in reaction
            const likedItem = get(reaction, ['style', 'items'], []).find((item) => {
                return item.data.isLiked;
            });

            // Find disliked item in reaction
            const dislikedItem = get(reaction, ['style', 'items'], []).find((item) => {
                return item.data.isDisliked;
            });

            if ((likedItem && type === ReactionType.Like) || (dislikedItem && type === ReactionType.Dislike)) {
                return true;
            }

            return false;
        });

        setReactedItems(newReacted);
    };

    return {
        updateAfterReaction,
        reactedItems,
        reactedItemsUnProcessed,
        setReactedItems,
        setUnProcessedReactedItems,
    };
};

const useGetUserReactions = (userId: string, reactionType: ReactionType, options: QueryHookOptions = {}) => {
    const dispatch = useDispatch();
    const processStyles = (response: ReactionAlgoliaQuery) => {
        const data: ReactionAlgoliaQuery = JSON.parse(JSON.stringify(response));
        // Adding slider index
        let reactions = data.reactions.hits;

        // Find items in styles
        const reactedStyles = reactions.filter((reaction) => reaction.collectionName === ReactionDocType.STYLE);
        const reactedItems = reactions.filter((reaction) => reaction.collectionName === ReactionDocType.ITEMS);
        const reactedStyleIds = map(reactedStyles, 'docId');
        const reactedItemIds = map(reactedItems, 'docId');

        reactions = reactions
            .filter((reaction) => {
                if (reaction.collectionName === ReactionDocType.ITEMS && reaction.contextType === 'style') {
                    return !!get(reaction, ['extendsProps', 'styleContext', 0], null);
                }
                if (reaction.collectionName === ReactionDocType.ITEMS && reaction.contextType === 'item') {
                    return !!get(reaction, ['extendsProps', 'itemData', 0]);
                }
                if (reaction.collectionName === ReactionDocType.STYLE) {
                    return !!get(reaction, ['extendsProps', 'styleData', 0]);
                }
                return true;
            })
            .map((reaction) => {
                if (reaction.collectionName === ReactionDocType.STYLE) {
                    return {
                        ...reaction,
                        style: {
                            ...get(reaction, ['extendsProps', 'styleData', 0]),
                            collection: 'styles',
                        },
                    };
                } else if (reaction.collectionName === ReactionDocType.ITEMS && reaction.contextType === 'style') {
                    return {
                        ...reaction,
                        style: get(reaction, ['extendsProps', 'styleContext', 0]),
                    };
                } else {
                    return {
                        ...reaction,
                        style: get(reaction, ['extendsProps', 'itemData', 0]),
                    };
                }
            });

        // Add like flag in items and styles
        reactions = reactions.map((reaction) => {
            // Check style is liked
            if (reaction.collectionName === ReactionDocType.STYLE) {
                if (reaction.type === ReactionType.Like) {
                    reaction.style.isLiked = reactedStyleIds.includes(reaction?.doc?.style?.id);
                } else reaction.style.isDisliked = reactedStyleIds.includes(reaction?.doc?.style?.id);
            } else if (reaction.collectionName === ReactionDocType.ITEMS && reaction.contextType !== 'style') {
                if (reaction.type === ReactionType.Like) {
                    reaction.style.isLiked = reactedItemIds.includes(reaction?.doc?.item?.id);
                } else reaction.style.isDisliked = reactedItemIds.includes(reaction?.doc?.item?.id);
            }
            if (get(reaction, ['style', 'items'])) {
                reaction.style.items = reaction.style.items.map((item) => {
                    if (reaction.type === ReactionType.Like) {
                        item.data.isLiked = reactedItemIds.includes(item.id);
                    } else item.data.isDisliked = reactedItemIds.includes(item.id);
                    return item;
                });
            }

            return reaction;
        });
        reactions = uniqBy(reactions, 'style.id');

        // Finding slider index of items to scroll to first item with liked style
        reactions = reactions.map((reaction) => {
            if (reaction.collectionName === ReactionDocType.STYLE) {
                reaction.sliderIndex = 0;
                reaction.style.sliderIndex = 0;
            } else if (reaction.collectionName === ReactionDocType.ITEMS && reaction.contextType === 'style') {
                for (let index = 0; index < reaction.style.items.length; index++) {
                    const element = reaction.style.items[index];
                    const isReacted = reactedItemIds.includes(element.id);
                    if (isReacted) {
                        reaction.sliderIndex = index + 1;
                        reaction.style.sliderIndex = index + 1;
                        break;
                    }
                }
            } else if (reaction.collectionName === ReactionDocType.ITEMS && reaction.contextType !== 'style') {
                reaction.sliderIndex = 0;
                reaction.extendsProps.itemData[0].sliderIndex = 0;
            }
            return reaction;
        });
        // Updating with processed data
        data.reactions.hits = reactions;
        return { hits: data.reactions.hits };
    };
    return useQuery<ReactionAlgoliaQuery>(USER_REACTIONS, {
        variables: {
            query: '',
            filters: `(context.style.status: private OR context.style.status: published  OR context.style.status: unlisted OR context.item.status: published OR context.item.status: available) AND type: ${reactionType} AND creator: ${userId}`,
        },
        onCompleted: (data) => {
            const processed = processStyles(data);
            dispatch(setReactedItemsAction(processed.hits));
            dispatch(setUnProcessedReactedItemsAction(processed.hits));
        },
        fetchPolicy: 'network-only',
        skip: !userId,
        ...options,
    });
};

export default useGetUserReactions;
